import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Sparkles, Target, Users, Zap, Heart } from 'lucide-react';

export default function AboutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const values = [
    {
      icon: <Target className="w-6 h-6 text-violet-500" />,
      title: t('about.values.mission.title', 'Our Mission'),
      description: t(
        'about.values.mission.description',
        'We believe learning should be fun, engaging, and accessible to everyone. QuizMind transforms knowledge into an exciting journey.'
      ),
    },
    {
      icon: <Zap className="w-6 h-6 text-fuchsia-500" />,
      title: t('about.values.innovation.title', 'Innovation'),
      description: t(
        'about.values.innovation.description',
        'We continuously improve our platform with cutting-edge technology to deliver the best quiz experience possible.'
      ),
    },
    {
      icon: <Users className="w-6 h-6 text-sky-500" />,
      title: t('about.values.community.title', 'Community'),
      description: t(
        'about.values.community.description',
        'Our growing community of curious learners inspires us every day. Together, we make knowledge more accessible.'
      ),
    },
    {
      icon: <Heart className="w-6 h-6 text-rose-500" />,
      title: t('about.values.passion.title', 'Passion'),
      description: t(
        'about.values.passion.description',
        'Every quiz is crafted with care and passion. We love what we do, and it shows in every question we create.'
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-100 via-fuchsia-50 to-sky-100">
      {/* Header */}
      <header className="bg-white/70 backdrop-blur-xl border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">QuizMind</h1>
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4" />
              {t('about.badge', 'About Us')}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
              {t('about.hero.title', 'Making Learning an Adventure')}
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              {t(
                'about.hero.description',
                'QuizMind was born from a simple idea: knowledge should be exciting. We build quizzes that challenge, entertain, and inspire curiosity across every subject imaginable.'
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-3xl p-8 md:p-12 border border-slate-100 shadow-sm"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              {t('about.story.title', 'Our Story')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t(
                'about.story.paragraph1',
                'QuizMind started as a passion project by a small team of educators and technologists who believed that traditional learning methods needed a refresh. We saw people struggling to stay engaged with dry textbooks and repetitive exercises.'
              )}
            </p>
            <p className="text-slate-600 leading-relaxed">
              {t(
                'about.story.paragraph2',
                'Today, QuizMind hosts hundreds of quizzes spanning science, history, culture, technology, and more — each designed to make you think, laugh, and leave a little wiser than before.'
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center"
          >
            {t('about.values.title', 'What We Stand For')}
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center mb-4">
                  {value.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{value.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              {t('about.cta.title', 'Ready to test your knowledge?')}
            </h2>
            <p className="text-slate-600 mb-8">
              {t('about.cta.description', 'Jump in and explore our collection of quizzes waiting for you.')}
            </p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold px-8 py-3 rounded-2xl hover:opacity-90 transition-opacity shadow-lg shadow-violet-200"
            >
              <Sparkles className="w-4 h-4" />
              {t('about.cta.button', 'Explore Quizzes')}
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white/50 backdrop-blur-sm border-t border-slate-100 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm text-slate-600">QuizMind</span>
            </div>
            <p className="text-sm text-slate-600">{t('footer.company')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}